/**
 * Typed client for the AICAPTION API. Session cookies are sent automatically (same origin).
 * Every failure surfaces as ApiError with the server's code + message; a 401 also tells the
 * auth store the session has ended.
 */
import type {
  ApiErrorBody,
  BrandPreset,
  ExportFormat,
  ExportQuality,
  ExportResolution,
  HealthResponse,
  Job,
  Project,
  ProjectSummary,
  PublicUser,
  SubtitleStyle,
  TranscribeRequest,
  UpdateProjectInput,
  UserPreferences,
  Word,
  TranslationTarget,
} from '@aicaption/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

let onUnauthorized: () => void = () => undefined;
/** Registered by the auth store so any 401 signs the user out of the UI. */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && typeof init.body === 'string') headers.set('content-type', 'application/json');
  let res: Response;
  try {
    res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Is the API running?');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | undefined)?.error;
    if (res.status === 401 && !path.startsWith('/api/auth/')) onUnauthorized();
    throw new ApiError(res.status, err?.code ?? 'HTTP_ERROR', err?.message ?? res.statusText, err?.details);
  }
  return data as T;
}

const json = (body: unknown) => JSON.stringify(body);

export interface ExportOptions {
  format: ExportFormat;
  resolution?: ExportResolution;
  quality?: ExportQuality;
}

export interface CheckoutResponse {
  payment: { id: string; amountPaise: number; walletAppliedPaise: number; discountPaise: number; status: string };
  checkout: { key: string; orderId: string; amountPaise: number; currency: string; name: string } | null;
}
export interface VerificationPending { verificationRequired: true; email: string; expiresAt: string; resendAfter: string; }

export interface BillingOverview {
  walletBalancePaise: number;
  availableSeconds: number;
  availableMinutes: number;
  subscriptionSeconds: number;
  topUpSeconds: number;
  referral: { code: string | null; link: string | null; pending: number; successful: number; earnedPaise: number };
  subscription: { id: string; planId: 'starter' | 'creator' | 'pro'; cycle: 'monthly' | 'yearly'; status: string; autoRenew: boolean; startedAt: string; expiresAt: string; razorpaySubscriptionId?: string } | null;
  payments: Array<{ id: string; productType: 'subscription' | 'top_up'; productId: string; amountPaise: number; status: string; createdAt: string }>;
}
export interface WalletTransaction { id: string; direction: 'credit' | 'debit'; amountPaise: number; description: string; createdAt: string; }
export interface Invoice { id: string; invoiceNumber: string; productId: string; amountPaise: number; currency: 'INR'; issuedAt: string; }
export interface CommunityReport { id: string; kind: 'bug' | 'idea'; category?: string; title: string; description: string; status: string; rewardMinutes: number; userId?: string; createdAt: string; reviewNote?: string | null; }
export interface AdminOverview { payments: Array<{ id: string; productId: string; status: string; amountPaise: number; createdAt: string }>; subscriptions: unknown[]; wallets: unknown[]; reports: unknown[]; audits: Array<{ id: string; action: string; createdAt: string }>; }
export interface AdminControlOverview {
  metrics: { totalUsers: number; activeSubscriptions: number; revenuePaise: number; walletLiabilityPaise: number; videosProcessing: number; videosFailed: number; aiMinutesConsumed: number; pendingBugs: number; pendingIdeas: number };
  charts: { revenue: Array<{ date: string; amountPaise: number }>; registrations: Array<{ date: string; users: number }>; paymentStates: Array<{ name: string; value: number }>; plans: Array<{ name: string; value: number }>; videoStates: Array<{ name: string; value: number }>; usage: Array<{ date: string; minutes: number }> };
  recentActivity: Array<{ id: string; action: string; detail: string; createdAt: string; actorUserId?: string }>;
}
export interface AdminPageResult<T = Record<string, unknown>> { items: T[]; page: number; limit: number; total: number; pages: number; }
export interface AdminUser { id: string; name: string; email: string; plan: string; createdAt: string; lastLoginAt?: string; }

export const api = {
  health: () => request<HealthResponse>('/api/health'),

  // ── Auth & account ──
  me: () => request<PublicUser>('/api/me'),
  signup: (body: { name: string; email: string; password: string }) =>
    request<PublicUser | VerificationPending>('/api/auth/signup', { method: 'POST', body: json(body) }),
  login: (body: { email: string; password: string }) =>
    request<PublicUser>('/api/auth/login', { method: 'POST', body: json(body) }),
  emailStatus: (email: string) => request<{ valid: true; available: boolean }>(`/api/auth/email-status?email=${encodeURIComponent(email)}`),
  verifyEmail: (body: { email: string; code: string }) => request<PublicUser>('/api/auth/verify-email', { method: 'POST', body: json(body) }),
  resendVerification: (email: string) => request<{ accepted: true }>('/api/auth/resend-verification', { method: 'POST', body: json({ email }) }),
  requestPasswordReset: (email: string) => request<{ accepted: true }>('/api/auth/password-reset/request', { method: 'POST', body: json({ email }) }),
  resetPassword: (body: { email: string; code: string; password: string }) => request<PublicUser>('/api/auth/password-reset/confirm', { method: 'POST', body: json(body) }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  updateMe: (body: { name?: string; preferences?: Partial<UserPreferences> }) =>
    request<PublicUser>('/api/me', { method: 'PATCH', body: json(body) }),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    request<PublicUser>('/api/me/password', { method: 'POST', body: json(body) }),
  addBrandPreset: (body: { name: string; style: SubtitleStyle }) =>
    request<BrandPreset>('/api/me/brand-presets', { method: 'POST', body: json(body) }),
  removeBrandPreset: (id: string) => request<PublicUser>(`/api/me/brand-presets/${id}`, { method: 'DELETE' }),
  checkout: (body: { productType: 'subscription' | 'top_up'; productId: string; cycle?: 'monthly' | 'yearly'; idempotencyKey: string }) =>
    request<CheckoutResponse>('/api/billing/checkout', { method: 'POST', body: json(body) }),
  verifyPayment: (body: { paymentId: string; razorpayOrderId: string; razorpayPaymentId: string; razorpaySignature: string }) =>
    request<CheckoutResponse['payment']>('/api/billing/verify-payment', { method: 'POST', body: json(body) }),
  setAutoRenew: (subscriptionId: string, autoRenew: boolean) =>
    request<BillingOverview['subscription']>(`/api/billing/subscriptions/${subscriptionId}/auto-renew`, { method: 'PATCH', body: json({ autoRenew }) }),
  billingOverview: () => request<BillingOverview>('/api/billing/overview'),
  walletTransactions: () => request<WalletTransaction[]>('/api/billing/wallet/transactions'),
  usageTransactions: () => request<unknown[]>('/api/billing/usage/transactions'),
  invoices: () => request<Invoice[]>('/api/billing/invoices'),
  invoiceDownloadUrl: (invoiceId: string) => `/api/billing/invoices/${encodeURIComponent(invoiceId)}/download`,
  reports: (kind?: 'bug' | 'idea') => request<CommunityReport[]>(`/api/community/reports${kind ? `?kind=${kind}` : ''}`),
  submitReport: (body: { kind: 'bug' | 'idea'; category?: 'minor' | 'major'; title: string; description: string }) =>
    request<CommunityReport>('/api/community/reports', { method: 'POST', body: json(body) }),
  adminOverview: () => request<AdminOverview>('/api/admin/overview'),
  adminReports: (kind?: 'bug' | 'idea') => request<CommunityReport[]>(`/api/admin/reports${kind ? `?kind=${kind}` : ''}`),
  reviewReport: (id: string, body: { status: 'verified' | 'rejected' | 'approved' | 'implemented'; reviewNote?: string }) =>
    request<CommunityReport>(`/api/admin/reports/${id}`, { method: 'PATCH', body: json(body) }),
  adminControlOverview: (params: { from?: string; to?: string } = {}) => request<AdminControlOverview>(`/api/admin/control/overview?${new URLSearchParams(params).toString()}`),
  adminUsers: (params: { page?: number; limit?: number; search?: string } = {}) => request<AdminPageResult<AdminUser>>(`/api/admin/control/users?${new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined).map(([key, value]) => [key, String(value)]))}`),
  adminUserDetail: (userId: string) => request<Record<string, unknown>>(`/api/admin/control/users/${encodeURIComponent(userId)}`),
  adminProjects: (params: { page?: number; limit?: number; search?: string; status?: string } = {}) => request<AdminPageResult>(`/api/admin/control/projects?${new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined).map(([key, value]) => [key, String(value)]))}`),
  adminBilling: (params: { page?: number; limit?: number; kind?: string } = {}) => request<AdminPageResult>(`/api/admin/control/billing?${new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined).map(([key, value]) => [key, String(value)]))}`),
  adminAdjustWallet: (userId: string, body: { direction: 'credit' | 'debit'; amountPaise: number; reason: string }) => request<Record<string, unknown>>(`/api/admin/control/users/${encodeURIComponent(userId)}/wallet-adjustments`, { method: 'POST', body: json(body) }),
  adminAddNote: (userId: string, note: string) => request<Record<string, unknown>>(`/api/admin/control/users/${encodeURIComponent(userId)}/notes`, { method: 'POST', body: json({ note }) }),

  // ── Projects ──
  listProjects: () => request<ProjectSummary[]>('/api/projects'),
  getProject: (id: string) => request<Project>(`/api/projects/${id}`),
  updateProject: (id: string, patch: UpdateProjectInput) =>
    request<Project>(`/api/projects/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteProject: (id: string) => request<void>(`/api/projects/${id}`, { method: 'DELETE' }),
  transcribe: (id: string, body: TranscribeRequest) =>
    request<Job>(`/api/projects/${id}/transcribe`, { method: 'POST', body: json(body) }),
  saveTranscript: (id: string, words: Word[]) =>
    request<Project>(`/api/projects/${id}/transcript`, { method: 'PATCH', body: json({ words }) }),
  translate: (id: string, target: TranslationTarget) =>
    request<Project>(`/api/projects/${id}/translate`, { method: 'POST', body: json({ target }) }),
  saveStyle: (id: string, style: SubtitleStyle) =>
    request<Project>(`/api/projects/${id}/style`, { method: 'PATCH', body: json(style) }),
  startExport: (id: string, options: ExportOptions) =>
    request<Job>(`/api/projects/${id}/export`, { method: 'POST', body: json(options) }),
  getJob: (id: string) => request<Job>(`/api/jobs/${id}`),

  sourceUrl: (id: string) => `/api/projects/${id}/media/source`,
  downloadUrl: (id: string, exportId: string) => `/api/projects/${id}/exports/${exportId}/download`,

  /** Upload with progress (fetch has no upload progress, so this uses XHR). */
  upload(
    file: File,
    fields: { languageCode?: string | null },
    onProgress: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<Project> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/projects');
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Upload failed — check your connection'));
      xhr.onabort = () => reject(new ApiError(0, 'ABORTED', 'Upload cancelled'));
      signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.onload = () => {
        let body: unknown;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          body = undefined;
        }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(body as Project);
        if (xhr.status === 401) onUnauthorized();
        const err = (body as ApiErrorBody | undefined)?.error;
        reject(new ApiError(xhr.status, err?.code ?? 'HTTP_ERROR', err?.message ?? 'Upload failed'));
      };
      const form = new FormData();
      if (fields.languageCode) form.append('languageCode', fields.languageCode);
      form.append('video', file);
      xhr.send(form);
    });
  },
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

/** Field-level messages from a VALIDATION_ERROR response, keyed by field path. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || err.code !== 'VALIDATION_ERROR' || !Array.isArray(err.details)) return {};
  const out: Record<string, string> = {};
  for (const d of err.details as { path: string; message: string }[]) out[d.path] ??= d.message;
  return out;
}
